import { graphql } from '@eluve/graphql.tada';

/**
 * Computes a display name for a template bundle from its parts.
 * Used as a fallback when no explicit displayName is set.
 */
export const getTemplateBundleDisplayName = (bundle: {
  displayName?: string | null;
  template?: { name?: string | null } | null;
  model_args?: { name?: string | null; modelType?: string | null } | null;
  output_template?: { name?: string | null } | null;
}): string => {
  if (bundle.displayName) return bundle.displayName;

  const promptName = bundle.template?.name ?? 'Unknown Prompt';
  const modelName =
    bundle.model_args?.name ?? bundle.model_args?.modelType ?? 'Unknown Model';
  const outputName = bundle.output_template?.name ?? 'None';
  return `${promptName} + ${modelName} + ${outputName}`;
};

export const templateBundleListItemFragment = graphql(`
  fragment TemplateBundleListItem on LlmModelPromptTemplates @_unmask {
    __typename
    id
    displayName
    isActive
    isDefault
    isDefaultForNewTenants
    createdAt
    template {
      id
      name
      outputType
    }
    model_args {
      id
      name
      modelType
      isActive
    }
    output_template {
      id
      name
      tenantId
      tenant {
        id
        name
      }
    }
    tenantTemplateBundlesAggregate(where: { isEnabled: { _eq: true } }) {
      aggregate {
        count
      }
    }
  }
`);

/**
 * Pre-flight query for the disable-bundle safety net: returns the count of
 * OTHER enabled bundles for the tenant (excluding the one being disabled).
 * Bundles are enabled for all tenants, and the resolver throws hard when a
 * tenant has zero enabled bundles, so we block the disable here before it can
 * put the tenant in that state.
 */
export const getTenantBundleDisableSafetyQuery = graphql(`
  query getTenantBundleDisableSafety($tenantId: uuid!, $bundleId: uuid!) {
    otherEnabled: tenantTemplateBundlesAggregate(
      where: {
        tenantId: { _eq: $tenantId }
        isEnabled: { _eq: true }
        llmModelPromptTemplateId: { _neq: $bundleId }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const getAllTemplateBundlesQuery = graphql(
  `
    query getAllTemplateBundles {
      llmModelPromptTemplates(
        where: { isBundle: { _eq: true } }
        orderBy: { createdAt: DESC }
      ) {
        ...TemplateBundleListItem
      }
    }
  `,
  [templateBundleListItemFragment],
);

export const templateBundleDetailFragment = graphql(`
  fragment TemplateBundleDetail on LlmModelPromptTemplates @_unmask {
    __typename
    id
    displayName
    isActive
    isDefault
    isDefaultForNewTenants
    createdAt
    updatedAt
    promptTemplateId
    modelArgsId
    outputTemplateId
    template {
      id
      name
      outputType
      description
      prompt_template_variants(
        where: { isCurrentDefault: { _eq: true } }
        limit: 1
      ) {
        promptTemplateId
        variantNumber
        template
        variantNotes
        updatedAt
      }
    }
    model_args {
      id
      name
      modelType
      args
      description
      isActive
    }
    output_template {
      id
      name
      llmOutputType
      description
      tenantId
      tenant {
        id
        name
      }
      variants(where: { isCurrentDefault: { _eq: true } }, limit: 1) {
        outputTemplateId
        variantNumber
        template
        variantNotes
        updatedAt
      }
    }
    tenant_template_bundles {
      tenantId
      llmModelPromptTemplateId
      isEnabled
      tenant {
        id
        name
      }
    }
  }
`);

export const getTemplateBundleByIdQuery = graphql(
  `
    query getTemplateBundleById($id: uuid!) {
      llmModelPromptTemplates(
        where: { id: { _eq: $id }, isBundle: { _eq: true } }
        limit: 1
      ) {
        ...TemplateBundleDetail
      }
    }
  `,
  [templateBundleDetailFragment],
);

export const getTemplateBundleCreateOptionsQuery = graphql(`
  query getTemplateBundleCreateOptions {
    promptTemplates(
      where: { isActive: { _eq: true }, outputType: { _eq: DYNAMIC_OUTPUT } }
      orderBy: { name: ASC }
    ) {
      id
      name
      outputType
      description
    }
    llmModelArgs(where: { isActive: { _eq: true } }, orderBy: { name: ASC }) {
      id
      name
      modelType
      args
      description
    }
    llmOutputTemplates(
      where: { isActive: { _eq: true }, tenantId: { _isNull: true } }
      orderBy: { name: ASC }
    ) {
      id
      name
      llmOutputType
      description
    }
  }
`);

export const updateTemplateBundleIsActiveMutation = graphql(
  `
    mutation updateTemplateBundleIsActive($id: uuid!, $isActive: Boolean!) {
      updateLlmModelPromptTemplatesByPk(
        pkColumns: { id: $id }
        _set: { isActive: $isActive }
      ) {
        ...TemplateBundleListItem
      }
    }
  `,
  [templateBundleListItemFragment],
);

export const updateTemplateBundleIsDefaultForNewTenantsMutation = graphql(
  `
    mutation updateTemplateBundleIsDefaultForNewTenants(
      $id: uuid!
      $isDefaultForNewTenants: Boolean!
    ) {
      updateLlmModelPromptTemplatesByPk(
        pkColumns: { id: $id }
        _set: { isDefaultForNewTenants: $isDefaultForNewTenants }
      ) {
        ...TemplateBundleListItem
      }
    }
  `,
  [templateBundleListItemFragment],
);

// Atomic deactivate: clearing isActive and isDefaultForNewTenants in one UPDATE
// prevents an (active=false, default=true) intermediate state.
export const deactivateTemplateBundleMutation = graphql(
  `
    mutation deactivateTemplateBundle($id: uuid!) {
      updateLlmModelPromptTemplatesByPk(
        pkColumns: { id: $id }
        _set: { isActive: false, isDefaultForNewTenants: false }
      ) {
        ...TemplateBundleListItem
      }
    }
  `,
  [templateBundleListItemFragment],
);

export const createTemplateBundleMutation = graphql(
  `
    mutation createTemplateBundle(
      $promptTemplateId: uuid!
      $modelArgsId: uuid!
      $outputTemplateId: uuid!
      $displayName: String!
      $isActive: Boolean!
    ) {
      insertLlmModelPromptTemplatesOne(
        object: {
          promptTemplateId: $promptTemplateId
          modelArgsId: $modelArgsId
          outputTemplateId: $outputTemplateId
          displayName: $displayName
          isActive: $isActive
          isBundle: true
        }
      ) {
        ...TemplateBundleListItem
      }
    }
  `,
  [templateBundleListItemFragment],
);

// --- Disable confirmation queries & mutations ---

export const getAffectedTenantsForBundleDisableQuery = graphql(`
  query getAffectedTenantsForBundleDisable($bundleId: uuid!) {
    tenantTemplateBundles(
      where: {
        llmModelPromptTemplateId: { _eq: $bundleId }
        isEnabled: { _eq: true }
      }
    ) {
      llmModelPromptTemplateId
      tenantId
      isEnabled
      tenant {
        id
        name
        tenantTemplateBundlesAggregate(
          where: {
            isEnabled: { _eq: true }
            llmModelPromptTemplateId: { _neq: $bundleId }
          }
        ) {
          aggregate {
            count
          }
        }
      }
    }
  }
`);

export const getAffectedUsersForBundleDisableQuery = graphql(`
  query getAffectedUsersForBundleDisable($bundleId: uuid!) {
    tenantUsers(
      where: { settings: { defaultTemplateBundleId: { _eq: $bundleId } } }
    ) {
      tenantId
      userId
      user {
        firstName
        lastName
        email
      }
      tenant {
        name
      }
    }
  }
`);

export const getAffectedUsersForTenantBundleDisableQuery = graphql(`
  query getAffectedUsersForTenantBundleDisable(
    $bundleId: uuid!
    $tenantId: uuid!
  ) {
    tenantUsers(
      where: {
        tenantId: { _eq: $tenantId }
        settings: { defaultTemplateBundleId: { _eq: $bundleId } }
      }
    ) {
      tenantId
      userId
      user {
        firstName
        lastName
        email
      }
    }
  }
`);

export const clearDefaultTemplateBundleIdMutation = graphql(`
  mutation clearDefaultTemplateBundleId($where: TenantUserSettingsBoolExp!) {
    updateTenantUserSettings(
      where: $where
      _set: { defaultTemplateBundleId: null }
    ) {
      affectedRows
      returning {
        __typename
        tenantId
        userId
        defaultTemplateBundleId
      }
    }
  }
`);

export const disableTenantTemplateBundlesMutation = graphql(`
  mutation disableTenantTemplateBundles($bundleId: uuid!) {
    updateTenantTemplateBundles(
      where: { llmModelPromptTemplateId: { _eq: $bundleId } }
      _set: { isEnabled: false }
    ) {
      affectedRows
      returning {
        __typename
        llmModelPromptTemplateId
        tenantId
        isEnabled
      }
    }
  }
`);

// --- Replacement bundle migration (ELU-5491) ---

export const replacementBundleOptionFragment = graphql(`
  fragment ReplacementBundleOption on LlmModelPromptTemplates @_unmask {
    __typename
    id
    displayName
    template {
      id
      name
    }
    model_args {
      id
      name
      modelType
    }
    output_template {
      id
      name
    }
  }
`);

export const getReplacementBundleOptionsQuery = graphql(
  `
    query getReplacementBundleOptions($bundleId: uuid!) {
      llmModelPromptTemplates(
        where: {
          isBundle: { _eq: true }
          isActive: { _eq: true }
          id: { _neq: $bundleId }
          # Only global bundles can replace a global bundle — a tenant-owned
          # bundle must never be pushed onto other organizations.
          output_template: { tenantId: { _isNull: true } }
        }
        orderBy: { displayName: ASC }
      ) {
        ...ReplacementBundleOption
      }
    }
  `,
  [replacementBundleOptionFragment],
);

export const getReplacementBundleOptionsForTenantQuery = graphql(
  `
    query getReplacementBundleOptionsForTenant(
      $bundleId: uuid!
      $tenantId: uuid!
    ) {
      llmModelPromptTemplates(
        where: {
          isBundle: { _eq: true }
          isActive: { _eq: true }
          id: { _neq: $bundleId }
          # Global bundles, plus this tenant's own — never another tenant's.
          _or: [
            { output_template: { tenantId: { _isNull: true } } }
            { output_template: { tenantId: { _eq: $tenantId } } }
          ]
        }
        orderBy: { displayName: ASC }
      ) {
        ...ReplacementBundleOption
      }
    }
  `,
  [replacementBundleOptionFragment],
);

export const migrateDefaultTemplateBundleIdMutation = graphql(`
  mutation migrateDefaultTemplateBundleId(
    $where: TenantUserSettingsBoolExp!
    $newBundleId: uuid!
  ) {
    updateTenantUserSettings(
      where: $where
      _set: { defaultTemplateBundleId: $newBundleId }
    ) {
      affectedRows
      returning {
        __typename
        tenantId
        userId
        defaultTemplateBundleId
      }
    }
  }
`);

// --- Orphan admin rule defaults cleanup (ENG-4302) ---
//
// On bundle disable, `template_bundle_defaults` rule rows pointing at the
// disabled bundle must either migrate to a replacement (if chosen) or be
// deleted, mirroring the existing tenant_user_settings cleanup. The resolver
// already filters orphans via INNER JOIN on is_enabled, but the rows would
// otherwise pile up forever.

export const clearTemplateBundleDefaultsMutation = graphql(`
  mutation clearTemplateBundleDefaults($where: TemplateBundleDefaultsBoolExp!) {
    deleteTemplateBundleDefaults(where: $where) {
      affectedRows
      returning {
        __typename
        id
        tenantId
        templateBundleId
      }
    }
  }
`);

export const migrateTemplateBundleDefaultsMutation = graphql(`
  mutation migrateTemplateBundleDefaults(
    $where: TemplateBundleDefaultsBoolExp!
    $newBundleId: uuid!
  ) {
    updateTemplateBundleDefaults(
      where: $where
      _set: { templateBundleId: $newBundleId }
    ) {
      affectedRows
      returning {
        __typename
        id
        tenantId
        templateBundleId
      }
    }
  }
`);

// Postgres bulk UPDATE is atomic, so any (tenant, specialty, language) slot
// that already has a row at the new bundle would roll back the migrate.
// Fetch both result sets and compute the intersection client-side to find
// colliders to pre-delete before the bulk migrate runs.
export const getCollidingTemplateBundleDefaultsQuery = graphql(`
  query getCollidingTemplateBundleDefaults(
    $oldBundleScope: TemplateBundleDefaultsBoolExp!
    $newBundleScope: TemplateBundleDefaultsBoolExp!
  ) {
    oldRows: templateBundleDefaults(where: $oldBundleScope) {
      __typename
      id
      tenantId
      specialty
      language
    }
    newRows: templateBundleDefaults(where: $newBundleScope) {
      __typename
      tenantId
      specialty
      language
    }
  }
`);

export const bulkUpsertTenantTemplateBundlesMutation = graphql(`
  mutation bulkUpsertTenantTemplateBundles(
    $objects: [TenantTemplateBundlesInsertInput!]!
  ) {
    insertTenantTemplateBundles(
      objects: $objects
      onConflict: {
        constraint: tenant_template_bundles_pkey
        updateColumns: [isEnabled]
      }
    ) {
      affectedRows
      returning {
        __typename
        tenantId
        llmModelPromptTemplateId
        isEnabled
        updatedAt
      }
    }
  }
`);

// --- Batch-add organizations to a bundle (ENG-4405) ---
//
// Server-side filtered org search for the "Add Organizations" dialog on the
// global bundle page. Mirrors FeatureFlagTesterDialog's name _ilike search and
// the _not: { tenant_template_bundles } pattern from
// getReplacementBundleOptionsForTenantQuery. The exclusion is enforced here
// so the hook stays thin and the query is the single source of truth:
//   - exclude orgs already enabled for THIS bundle (a disabled row is kept so
//     the bulk upsert can flip it back to true)
// (The TEMPLATE_BUNDLES feature-flag filter was retired in ENG-3819 — bundles
// are enabled for every org, so no flag filter is the correct semantics.)
// Capped at 100; the dialog requires a search term and surfaces the cap so
// "select all matching" is never mistaken for "every org in the system".
export const searchOrganizationsForBundleQuery = graphql(`
  query searchOrganizationsForBundle($bundleId: uuid!, $search: String!) {
    tenants(
      orderBy: { name: ASC }
      where: {
        _and: [
          { deletedAt: { _isNull: true } }
          { name: { _ilike: $search } }
          {
            _not: {
              tenant_template_bundles: {
                llmModelPromptTemplateId: { _eq: $bundleId }
                isEnabled: { _eq: true }
              }
            }
          }
        ]
      }
      limit: 100
    ) {
      __typename
      id
      name
    }
  }
`);

// --- Global default bundle (ENG-4368) ---
// The single tenant_id IS NULL row in template_bundle_defaults is the
// system-wide safety net resolved when a bundles tenant has no enabled
// bundle. There is at most one such row (partial unique index), so the
// admin UI reads it and either updates or inserts.

export const getGlobalDefaultBundleQuery = graphql(`
  query getGlobalDefaultBundle {
    templateBundleDefaults(where: { tenantId: { _isNull: true } }, limit: 1) {
      __typename
      id
      templateBundleId
    }
  }
`);

export const updateGlobalDefaultBundleMutation = graphql(`
  mutation updateGlobalDefaultBundle($id: uuid!, $templateBundleId: uuid!) {
    updateTemplateBundleDefaultsByPk(
      pkColumns: { id: $id }
      _set: { templateBundleId: $templateBundleId }
    ) {
      __typename
      id
      templateBundleId
    }
  }
`);

export const insertGlobalDefaultBundleMutation = graphql(`
  mutation insertGlobalDefaultBundle($templateBundleId: uuid!) {
    insertTemplateBundleDefaultsOne(
      object: { tenantId: null, templateBundleId: $templateBundleId }
    ) {
      __typename
      id
      templateBundleId
    }
  }
`);

export const updateTemplateBundleDisplayNameMutation = graphql(
  `
    mutation updateTemplateBundleDisplayName(
      $id: uuid!
      $displayName: String!
    ) {
      updateLlmModelPromptTemplatesByPk(
        pkColumns: { id: $id }
        _set: { displayName: $displayName }
      ) {
        ...TemplateBundleDetail
      }
    }
  `,
  [templateBundleDetailFragment],
);
